import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization, useClerk, useUser } from '@clerk/clerk-react';
import { authApi } from '../../lib/api';
import StoresTab from './StoresTab';
import AccountTab from './AccountTab';
import LogoTab from './LogoTab';
import LanguageSelector from '../../components/LanguageSelector';

function SignOutButton() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="rounded-full focus:outline-none">
        {user?.imageUrl ? (
          <img src={user.imageUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full gradient-btn" />
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-48 rounded-xl shadow-lg z-50 overflow-hidden"
            style={{ background: 'rgba(4,11,19,0.98)', border: '1px solid rgba(142,152,223,0.2)' }}
          >
            <div className="px-4 py-3 text-xs text-white/40 truncate border-b border-white/10">
              {user?.primaryEmailAddress?.emailAddress}
            </div>
            <button
              onClick={() => void signOut({ redirectUrl: '/sign-in' })}
              className="flex items-center gap-2 w-full px-4 py-3 text-sm text-white hover:bg-white/5 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const [showSettings, setShowSettings] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [hasToken, setHasToken] = useState(true);
  const [name, setName] = useState('');
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [deleteLogoRequested, setDeleteLogoRequested] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authApi
      .getMe()
      .then((me) => {
        setLogoUrl(me.logoUrl);
        setName(me.name);
        setHasToken(me.hasToken);
      })
      .catch(() => null);
  }, []);

  function openSettings() {
    setPendingLogoFile(null);
    setDeleteLogoRequested(false);
    setShowSettings(true);
  }

  async function saveChanges() {
    setSaving(true);
    try {
      if (deleteLogoRequested) {
        await authApi.deleteLogo();
        setLogoUrl(null);
      } else if (pendingLogoFile) {
        const res = await authApi.uploadLogo(pendingLogoFile);
        setLogoUrl(res.logoUrl);
      }
      await authApi.updateAccount({ name });
      setShowSettings(false);
    } catch {
      // handled by global toast
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen p-4 md:p-6">
      {/* Header */}
      <header className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={organization?.name}
                className="w-11 h-11 rounded-xl object-cover"
                style={{ border: '2px solid rgba(142,152,223,0.3)' }}
              />
            ) : (
              <div className="w-11 h-11 rounded-xl flex items-center justify-center gradient-btn">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                </svg>
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-white">{organization?.name ?? t('appName')}</h1>
              <p className="text-gray-400 text-sm">{t('adminPanel')}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <button
              onClick={openSettings}
              className="text-gray-400 hover:text-white transition-colors p-2"
              title={t('accountSettings')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>
            <SignOutButton />
          </div>
        </div>
      </header>

      {/* Token warning */}
      {!hasToken && (
        <div
          className="max-w-4xl mx-auto mb-4 px-4 py-3 rounded-lg text-sm text-yellow-300 flex items-center gap-2"
          style={{ background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.3)' }}
        >
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
          {t('noTokenWarning')}
        </div>
      )}

      {/* Store list */}
      <main className="max-w-4xl mx-auto">
        <StoresTab />
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto mt-12 text-center">
        <p className="text-gray-500 text-sm">{t('footer')}</p>
        <p className="text-gray-600 text-xs mt-2">
          Powered by{' '}
          <a
            href="https://soundraw.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#29ABE2] hover:text-gray-400 transition-colors"
          >
            SOUNDRAW
          </a>
        </p>
      </footer>

      {/* Account Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            style={{ background: 'rgba(4,11,19,0.95)', border: '1px solid rgba(142,152,223,0.2)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">{t('accountSettings')}</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Logo section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">{t('companyLogo')}</h3>
              <LogoTab
                currentLogoUrl={logoUrl}
                pendingFile={pendingLogoFile}
                deleteRequested={deleteLogoRequested}
                onFileSelect={(f) => {
                  setPendingLogoFile(f);
                  setDeleteLogoRequested(false);
                }}
                onDeleteRequest={() => {
                  setDeleteLogoRequested(true);
                  setPendingLogoFile(null);
                }}
              />
            </div>

            {/* Account section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t('editAccount')}</h3>
              <AccountTab name={name} onNameChange={setName} />
            </div>

            <button
              onClick={() => void saveChanges()}
              disabled={saving}
              className="gradient-btn w-full py-2 rounded-lg text-white font-medium disabled:opacity-50"
            >
              {saving ? '...' : t('updateAccount')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
